import { ComponentProps, useState } from "react";
import { useInView } from "react-intersection-observer";
import { btLogger } from "@api/internal/utils/helpers";
import { QueryBody, ResponseErrorData } from "@api/internal/utils/types";
import { FeedTagResponseData } from "@pages/Keyword/utils/types";
import { sanitizeFeedSectionItems } from "@pages/Section/utils/sanitizeFeedSectionItems";
import { FeedSectionResponseData } from "@pages/Section/utils/types";
import Card from "@particles/card/Card";
import { SanitizedFeedSectionArticle } from "@typings/Cue";
import axios from "axios";

type useListingLoadMoreStoriesProps = {
  /** The initial list of articles to display. */
  initialList: ComponentProps<typeof Card>[];
  /** The template props to use for the articles. */
  templateProps: (
    cardProps: SanitizedFeedSectionArticle[]
  ) => ComponentProps<typeof Card>[];
} & Omit<QueryBody, "page">;

/**
 * Custom hook to manage loading more stories for listing pages.
 * This hook handles the logic for loading more stories when the user scrolls to the bottom of the page.
 * It uses the Intersection Observer API to detect when the user has scrolled to the bottom and fetches more stories accordingly.
 */
export const useListingLoadMoreStories = ({
  initialList,
  templateProps,
  size = 10,
  urlPath,
  sections,
  pageType,
}: useListingLoadMoreStoriesProps) => {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [loadMoreData, setLoadMoreData] = useState<
    ComponentProps<typeof Card>[]
  >([]);
  const [clickableHandleLoadMore, setClickableHandleLoadMore] =
    useState<boolean>(true);

  const handleLoadMore = async (data: ComponentProps<typeof Card>[]) => {
    setClickableHandleLoadMore(false);
    const page = [...data, ...loadMoreData].length / size + 1;

    const response: FeedTagResponseData | FeedSectionResponseData =
      await fetchListingStories({
        urlPath,
        page,
        size,
        sections,
        pageType,
      });

    if (!response.success) {
      setHasMoreStories(false);
      return;
    }

    const moreArticles = sanitizeFeedSectionItems(response.data.items);
    const articleProps = templateProps(moreArticles);

    setLoadMoreData([...loadMoreData, ...articleProps]);

    if (moreArticles.length < size) {
      setHasMoreStories(false);
    }

    setClickableHandleLoadMore(true);
  };

  const { ref } = useInView({
    threshold: 0,
    onChange: (inView) => {
      if (!inView) return;
      if (!hasMoreStories) return;
      if (initialList.length < size) return;

      handleLoadMore(initialList);
    },
  });

  return {
    ref,
    handleLoadMore,
    hasMoreStories,
    loadMoreData,
    clickableHandleLoadMore,
  };
};

/**
 * Fetches listing stories based on the provided query parameters.
 * @param urlPath - The URL path for the listing.
 * @param page - The page number to fetch.
 * @param size - The number of articles per page.
 * @param sections - The section to filter articles by.
 * @returns A promise that resolves to the fetched FeedTagResponseData.
 * @TODO: Update endpoint to support different page types and use backend utility functions
 */
const fetchListingStories = async ({
  urlPath,
  page,
  size,
  sections,
  pageType,
}: QueryBody): Promise<FeedSectionResponseData | FeedTagResponseData> => {
  const payload = {
    urlPath,
    page,
    size,
    sections,
  };

  const endpoint =
    pageType === "keyword"
      ? "/_plat/api/v1/articles/tags"
      : "/_plat/api/v1/articles/sections";

  const response = await axios
    .get<FeedSectionResponseData | FeedTagResponseData>(endpoint, {
      params: payload,
    })
    .then(({ data }) => data)
    .catch((error) => {
      btLogger({
        type: "error",
        message: `Error: fetchListingStories - ${error.message} ${JSON.stringify({ payload })}`,
      });

      return {
        success: false,
        error: { code: error.response?.status, message: error.message },
      } as ResponseErrorData;
    });

  return response;
};
